import { BaseFeature } from '../../core/base-feature.js';

/**
 * ScreenshotFeature - Click-and-drag region screenshot capture
 *
 * Handles:
 * - Drag-to-select rectangle on video element
 * - Coordinate calculation and sending to desktop
 * - Displaying captured screenshots in modal
 * - Integration with timeline for screenshot events
 */
export class ScreenshotFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.isDragging = false;
        this.dragStart = null;
        this.dragEnd = null;
        this.videoElement = null;
        this.videoWrapper = null;
        this.selectionOverlay = null;
        this.pendingCaptures = new Map(); // messageId -> callback
        this.blockNextClick = false; // Block click after drag completes

        // Native screen resolution (from window-control)
        this.nativeScreenWidth = null;
        this.nativeScreenHeight = null;

        // Latest captured screenshot for display
        this.lastScreenshot = null;
    }

    static getMetadata() {
        return {
            id: 'screenshot',
            name: 'screenshot',
            version: 'v1.0',
            capabilities: [
                'capture_region',
                'display_screenshot'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Screenshot] Initializing...');

        // Listen for native resolution updates from window-control
        this.handleWindowInfo = (windowInfo) => {
            if (windowInfo && windowInfo.screenWidth && windowInfo.screenHeight) {
                this.nativeScreenWidth = windowInfo.screenWidth;
                this.nativeScreenHeight = windowInfo.screenHeight;
            }
        };

        console.log('[Screenshot] ✓ Initialized');
    }

    /**
     * Set up drag handler on video element
     * Called from ScreenSharePanel when video is available
     */
    setupDragHandler(videoElement) {
        if (!videoElement || this.videoElement === videoElement) {
            return;
        }

        // Remove existing handlers if any
        if (this.videoElement) {
            this.removeDragHandlers();
        }

        this.videoElement = videoElement;

        // Create selection overlay
        this.createSelectionOverlay();

        // Bind handlers
        this._handleMouseDown = this.handleMouseDown.bind(this);
        this._handleMouseMove = this.handleMouseMove.bind(this);
        this._handleMouseUp = this.handleMouseUp.bind(this);
        this._handleClick = this.handleClick.bind(this);

        // Add event listeners
        videoElement.addEventListener('mousedown', this._handleMouseDown);
        document.addEventListener('mousemove', this._handleMouseMove);
        document.addEventListener('mouseup', this._handleMouseUp);
        // Capture click to prevent window-control from handling after a drag
        videoElement.addEventListener('click', this._handleClick, true);

        console.log('[Screenshot] Drag handler set up on video element');
    }

    /**
     * Remove drag handlers
     */
    removeDragHandlers() {
        if (this.videoElement) {
            if (this._handleMouseDown) {
                this.videoElement.removeEventListener('mousedown', this._handleMouseDown);
            }
            if (this._handleClick) {
                this.videoElement.removeEventListener('click', this._handleClick, true);
            }
        }
        if (this._handleMouseMove) {
            document.removeEventListener('mousemove', this._handleMouseMove);
        }
        if (this._handleMouseUp) {
            document.removeEventListener('mouseup', this._handleMouseUp);
        }

        if (this.selectionOverlay && this.selectionOverlay.parentNode) {
            this.selectionOverlay.remove();
        }

        this.selectionOverlay = null;
        this.videoWrapper = null;
        this.videoElement = null;
        this._handleMouseDown = null;
        this._handleMouseMove = null;
        this._handleMouseUp = null;
        this._handleClick = null;

        console.log('[Screenshot] Drag handlers removed');
    }

    /**
     * Create the selection overlay element
     */
    createSelectionOverlay() {
        if (!this.videoElement) return;

        // Find the video wrapper (parent of video element)
        const videoWrapper = this.videoElement.parentElement;
        if (!videoWrapper) return;

        this.videoWrapper = videoWrapper;

        // Ensure wrapper is positioned
        if (videoWrapper.style.position !== 'absolute' && videoWrapper.style.position !== 'relative') {
            videoWrapper.style.position = 'relative';
        }

        // Remove any existing overlay
        const existingOverlay = videoWrapper.querySelector('.screenshot-selection-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        // Create overlay
        this.selectionOverlay = document.createElement('div');
        this.selectionOverlay.className = 'screenshot-selection-overlay';
        this.selectionOverlay.style.cssText = `
            position: absolute;
            border: 2px dashed #d97706;
            background: rgba(217, 119, 6, 0.15);
            pointer-events: none;
            z-index: 200;
            display: none;
            box-sizing: border-box;
            box-shadow: 0 0 12px rgba(217, 119, 6, 0.3);
            border-radius: 4px;
        `;

        videoWrapper.appendChild(this.selectionOverlay);
        console.log('[Screenshot] Selection overlay created');
    }

    /**
     * Handle mouse down - start drag
     */
    handleMouseDown(e) {
        // Only handle left click
        if (e.button !== 0) return;

        // Check if it should be a click (window selection) or drag (screenshot)
        // We'll start tracking, and decide based on movement
        const rect = this.videoElement.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        this.dragStart = { x, y, clientX: e.clientX, clientY: e.clientY };
        this.dragEnd = null;
        this.dragStartTime = Date.now();

        // Don't prevent default yet - allow click through for window selection
    }

    /**
     * Handle mouse move - update selection
     */
    handleMouseMove(e) {
        if (!this.dragStart) return;

        const rect = this.videoElement.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Check if we've moved enough to start a drag (threshold: 10px)
        const dx = Math.abs(e.clientX - this.dragStart.clientX);
        const dy = Math.abs(e.clientY - this.dragStart.clientY);

        if (!this.isDragging && (dx > 10 || dy > 10)) {
            // Start dragging
            this.isDragging = true;

            // Prevent the click from reaching window-control
            e.preventDefault();
            e.stopPropagation();
        }

        if (this.isDragging) {
            this.dragEnd = { x, y };
            this.updateSelectionOverlay();
        }
    }

    /**
     * Handle mouse up - end drag and capture
     */
    handleMouseUp(e) {
        if (!this.dragStart) return;

        if (this.isDragging && this.dragEnd) {
            // Complete the screenshot capture
            e.preventDefault();
            e.stopPropagation();

            // Set flag to block the following click event
            this.blockNextClick = true;

            this.captureSelectedRegion();
        }

        // Reset state
        this.isDragging = false;
        this.dragStart = null;
        this.dragEnd = null;

        // Hide selection overlay
        if (this.selectionOverlay) {
            this.selectionOverlay.style.display = 'none';
        }
    }

    /**
     * Handle click - block click events after a drag
     * This prevents window-control from selecting a window after a screenshot drag
     */
    handleClick(e) {
        if (this.blockNextClick) {
            e.preventDefault();
            e.stopPropagation();
            this.blockNextClick = false;
            console.log('[Screenshot] Blocked click after drag');
        }
    }

    /**
     * Update the selection overlay position
     */
    updateSelectionOverlay() {
        if (!this.selectionOverlay || !this.dragStart || !this.dragEnd) return;

        const x1 = Math.min(this.dragStart.x, this.dragEnd.x);
        const y1 = Math.min(this.dragStart.y, this.dragEnd.y);
        const x2 = Math.max(this.dragStart.x, this.dragEnd.x);
        const y2 = Math.max(this.dragStart.y, this.dragEnd.y);

        this.selectionOverlay.style.display = 'block';
        this.selectionOverlay.style.left = `${x1}px`;
        this.selectionOverlay.style.top = `${y1}px`;
        this.selectionOverlay.style.width = `${x2 - x1}px`;
        this.selectionOverlay.style.height = `${y2 - y1}px`;
    }

    /**
     * Get native screen resolution from window-control feature
     * Window-control receives this from actual desktop responses
     */
    getNativeResolution() {
        // Try to get from window-control feature (most reliable - from desktop responses)
        const featureManager = this.core.getFeatureManager?.();
        const windowControl = featureManager?.getFeature?.('window-control');

        if (windowControl?.nativeScreenWidth && windowControl?.nativeScreenHeight) {
            return {
                width: windowControl.nativeScreenWidth,
                height: windowControl.nativeScreenHeight
            };
        }

        // Fall back to our stored values
        if (this.nativeScreenWidth && this.nativeScreenHeight) {
            return {
                width: this.nativeScreenWidth,
                height: this.nativeScreenHeight
            };
        }

        // Last resort: video dimensions (not ideal - may be scaled)
        if (this.videoElement) {
            return {
                width: this.videoElement.videoWidth || this.videoElement.clientWidth,
                height: this.videoElement.videoHeight || this.videoElement.clientHeight
            };
        }

        return { width: null, height: null };
    }

    /**
     * Capture the selected region
     */
    captureSelectedRegion() {
        if (!this.videoElement || !this.dragStart || !this.dragEnd) return;

        // Get display dimensions (CSS rendered size)
        const displayWidth = this.videoElement.clientWidth;
        const displayHeight = this.videoElement.clientHeight;

        // Get native screen resolution from window-control (most reliable source)
        const nativeRes = this.getNativeResolution();
        const targetWidth = nativeRes.width || this.videoElement.videoWidth || displayWidth;
        const targetHeight = nativeRes.height || this.videoElement.videoHeight || displayHeight;

        // Calculate rendered video area accounting for object-fit: contain letterboxing
        const videoAspect = targetWidth / targetHeight;
        const containerAspect = displayWidth / displayHeight;

        let renderedWidth, renderedHeight, offsetX, offsetY;

        if (videoAspect > containerAspect) {
            // Video wider than container - letterbox top/bottom
            renderedWidth = displayWidth;
            renderedHeight = displayWidth / videoAspect;
            offsetX = 0;
            offsetY = (displayHeight - renderedHeight) / 2;
        } else {
            // Video taller than container - letterbox left/right
            renderedHeight = displayHeight;
            renderedWidth = displayHeight * videoAspect;
            offsetX = (displayWidth - renderedWidth) / 2;
            offsetY = 0;
        }

        // Get selection bounds in display coordinates (relative to video element)
        const rawX1 = Math.min(this.dragStart.x, this.dragEnd.x);
        const rawY1 = Math.min(this.dragStart.y, this.dragEnd.y);
        const rawX2 = Math.max(this.dragStart.x, this.dragEnd.x);
        const rawY2 = Math.max(this.dragStart.y, this.dragEnd.y);

        // Adjust for letterboxing offset - convert to position relative to actual video content
        const videoX1 = Math.max(0, rawX1 - offsetX);
        const videoY1 = Math.max(0, rawY1 - offsetY);
        const videoX2 = Math.min(renderedWidth, rawX2 - offsetX);
        const videoY2 = Math.min(renderedHeight, rawY2 - offsetY);

        // Calculate normalized coordinates (0-1) based on rendered video area
        const normalizedX1 = videoX1 / renderedWidth;
        const normalizedY1 = videoY1 / renderedHeight;
        const normalizedX2 = videoX2 / renderedWidth;
        const normalizedY2 = videoY2 / renderedHeight;

        // Convert to screen coordinates
        const screenX1 = Math.round(normalizedX1 * targetWidth);
        const screenY1 = Math.round(normalizedY1 * targetHeight);
        const screenX2 = Math.round(normalizedX2 * targetWidth);
        const screenY2 = Math.round(normalizedY2 * targetHeight);

        console.log(`[Screenshot] Display: ${displayWidth}x${displayHeight}, rendered: ${renderedWidth.toFixed(0)}x${renderedHeight.toFixed(0)}, offset: (${offsetX.toFixed(0)},${offsetY.toFixed(0)})`);
        console.log(`[Screenshot] Screen coords: (${screenX1},${screenY1}) to (${screenX2},${screenY2}), native: ${targetWidth}x${targetHeight}`);

        // Send capture request to desktop
        const messageId = this.sendMessage('capture_region', {
            x1: screenX1,
            y1: screenY1,
            x2: screenX2,
            y2: screenY2,
            nativeWidth: targetWidth,
            nativeHeight: targetHeight
        });

        console.log(`[Screenshot] Capture request sent, messageId: ${messageId}`);
    }

    /**
     * Handle incoming messages
     */
    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'capture_region_response':
                return this.handleCaptureResponse(payload, messageId, fromId);

            case 'capture_fullscreen_response':
                return this.handleCaptureResponse(payload, messageId, fromId);

            default:
                console.warn(`[Screenshot] Unknown action: ${action}`);
        }
    }

    /**
     * Handle capture response from desktop
     */
    handleCaptureResponse(payload, messageId, fromId) {
        console.log('[Screenshot] Received capture response:', payload.success, payload.width, 'x', payload.height);

        if (!payload.success) {
            console.error('[Screenshot] Capture failed:', payload.error);
            this.emit('captureError', { error: payload.error });
            return;
        }

        // Store the screenshot
        this.lastScreenshot = {
            eventId: payload.eventId,
            width: payload.width,
            height: payload.height,
            mimeType: payload.mimeType,
            imageData: payload.imageData,
            timestamp: payload.timestamp,
            region: payload.region
        };

        // Emit event for UI to display
        this.emit('screenshotCaptured', this.lastScreenshot);

        console.log(`[Screenshot] Screenshot captured: ${payload.width}x${payload.height}, eventId: ${payload.eventId}`);
    }

    /**
     * Update native resolution (called from window-control feature)
     */
    updateNativeResolution(width, height) {
        this.nativeScreenWidth = width;
        this.nativeScreenHeight = height;
    }

    /**
     * Get the last captured screenshot
     */
    getLastScreenshot() {
        return this.lastScreenshot;
    }

    /**
     * Clear the last screenshot
     */
    clearLastScreenshot() {
        this.lastScreenshot = null;
        this.emit('screenshotCleared');
    }

    async cleanup() {
        console.log('[Screenshot] Cleaning up...');
        this.removeDragHandlers();
        this.pendingCaptures.clear();
        this.lastScreenshot = null;
    }
}
